import type React from 'react';
import { useMemo } from 'react';
import { TooltipOnTruncatedCell } from '@components/TooltipOnTruncated';
import { isBlockInactive } from '@root/helpers/blocks';
import { type Cell, flexRender } from '@tanstack/react-table';
import { get } from 'lodash-es';
import { twMerge } from 'tailwind-merge';

const cellBaseClasses = 'truncate whitespace-nowrap px-3 py-4 align-middle text-sm text-info-500';
interface TableCellProps<TData, TValue> {
  cell: Cell<TData, TValue>;
}

export const TableCell = <TData, TValue>({ cell }: TableCellProps<TData, TValue>) => {
  const { classes, tooltip, style } = useMemo(() => {
    const tooltip = cell.column.columnDef.meta?.tooltip;
    const inceptionDate = String(get(cell.row.original, 'inception_date', ''));
    const expiryDate = String(get(cell.row.original, 'expiry_date', ''));
    const isInactive = isBlockInactive(inceptionDate, expiryDate);
    const classes = twMerge(
      cellBaseClasses,
      cell.column.columnDef?.meta?.dataCellClassName,
      cell.column.getIsPinned() &&
        'transition-colors duration-150 sticky [filter:drop-shadow(-2px_0_2px_rgba(0,0,0,0.1))] bg-white group-hover:bg-blue-50',
      (get(cell.row.original, 'deleted_at') || isInactive) && 'text-gray-400',
    );

    const columnDef = cell.column.columnDef;
    const hasMaxSize = columnDef.maxSize !== undefined && columnDef.maxSize !== Number.MAX_SAFE_INTEGER;
    const hasMinSize = columnDef.minSize !== undefined;
    const isFlexible = !hasMaxSize && hasMinSize;

    const style: React.CSSProperties = {
      ...(hasMaxSize
        ? {
            width: `${columnDef.maxSize}px`,
            minWidth: `${columnDef.maxSize}px`,
            maxWidth: `${columnDef.maxSize}px`,
          }
        : isFlexible
          ? {
              width: '100%',
              minWidth: `${columnDef.minSize}px`,
            }
          : {
              width: `${cell.column.getSize()}px`,
            }),
      ...(cell.column.getIsPinned() === 'right' && { right: cell.column.getAfter('right') }),
      ...(cell.column.getIsPinned() === 'left' && { left: cell.column.getStart('left') }),
    };

    return {
      tooltip,
      classes,
      style,
    };
  }, [cell]);

  const content = flexRender(cell.column.columnDef.cell, cell.getContext());

  if (!tooltip) {
    return (
      <td className={classes} style={style}>
        {content}
      </td>
    );
  }

  return (
    <TooltipOnTruncatedCell tooltipContent={content} className={classes} style={style}>
      {content}
    </TooltipOnTruncatedCell>
  );
};
